import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useRootStore } from 'store';
import {
  TablePaginatedV8,
  EditCellV8,
  TableCellV8,
  TableEditButtons,
  promiseModalDialog,
  toastSuccess,
} from 'motion-components';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { useFormikContext } from 'formik';
import { useGetContainersAsOptionsQuery } from 'shared-pages/components';
import { gridInactiveRowStyle, gridRowSelectedStyle } from 'shared-pages/common/grid-helpfunc';
import { useMutation } from '@apollo/client';
import { gqlDeleteCollectionOrderContainer } from '../graphql';

/* ----------- Component ------------------ */
export const OrderExtrasTable = ({ data, canEdit }) => {
  const location = useLocation<any>();
  const history = useHistory();
  const { appStore, tableStore } = useRootStore();
  const { t } = useTranslation();
  const { values, setFieldValue, errors } = useFormikContext<any>();

  const onChangeContainerType = useCallback(
    (value, row) => {
      const rowIndex = row.index;
      const containertypeName = row?.valueName ?? '';

      setFieldValue(`collectionOrdersContainers[${rowIndex}]`, {
        ...row.original,
        containertypeId: value,
        containertype: {
          id: value,
          name: containertypeName,
        },
      });
    },
    [values, setFieldValue],
  );

  // Define Columns (Without Weight)
  const columns = useMemo(
    () => [
      {
        id: 'containertype.name',
        header: t('Τύπος Συσκευασίας'),
        columnName: t('Τύπος Συσκευασίας'),
        accessorKey: 'containertypeId',
        orderByField: 'containertype.name',
        cell: TableCellV8,
        size: 30,
        meta: {
          type: 'select',
          idKey: 'containertypeId',
          idName: 'containertype.name',
          hook: useGetContainersAsOptionsQuery,
          onValueChange: onChangeContainerType,
          where: JSON.stringify({
            isActive: 1,
          }),
        },
      },
      {
        id: 'containersQty',
        header: t('Πλήθος Συσκευασιών'),
        columnName: t('Πλήθος Συσκευασιών'),
        accessorKey: 'containersQty',
        orderByField: 'containersQty',
        cell: TableCellV8,
        meta: { type: 'float' },
        size: 30,
      },
      {
        id: 'edit',
        header: t('Ενέργειες'),
        columnName: t('Ενέργειες'),
        accessorKey: 'edit',
        size: 120,
        cell: (props: any) => {
          const { row, table } = props;
          return canEdit ? EditCellV8({ row, table }) : <>-</>;
        },
      },
    ],
    [t],
  );
  // For row selection or highlighting
  const [selected, setSelected] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  // Save Row Logic (Without Weight Calculations)
  const onSaveRow = async (rowIndex, row, changes) => {
    try {
      setLoading(true);

      const lines = [...values?.collectionOrdersContainers];
      const existingLine = lines[rowIndex];

      // If new row => add it to Formik
      if (!existingLine) {
        // create a new row object
        const newLine = {
          id: null,
          containertypeId: changes.containertypeId ?? null,
          containersQty: changes.containersQty ? parseInt(changes.containersQty, 10) : 0,
        };
        lines.push(newLine);
      } else {
        // update existing row
        lines[rowIndex] = {
          ...existingLine,
          ...changes,
          containertypeId: changes?.containertypeId
            ? changes?.containertypeId
            : existingLine.containertypeId,
          containersQty: changes.containersQty
            ? parseInt(changes.containersQty, 10)
            : existingLine.containersQty,
        };
      }

      setFieldValue('collectionOrdersContainers', lines);
    } catch (err) {
      console.error('Error in onSaveRow:', err);
    } finally {
      setLoading(false);
    }
    return true;
  };
  const gridRef = useRef(null);

  const [deleteContainer] = useMutation(gqlDeleteCollectionOrderContainer);
  /**
   *  Delete Συσκευασία
   * @param row
   */
  const handleDeleteRow = async (row: any, index: number): Promise<boolean> => {
    console.log('delete row', row, index);
    // Use row directly as your line object
    const lineObj = row;
    if (!lineObj) return false;

    // If new (undefined or null `id`):
    if (!lineObj.id) {
      console.log('row', row);
      const lines = [...values.collectionOrdersContainers];
      if (index > -1) lines.splice(index, 1);
      setFieldValue('collectionOrdersContainers', lines);
      return true;
    }

    // Otherwise, confirm + delete on the server
    const answer = await promiseModalDialog({
      title: t('Διαγραφή Εγγραφής'),
      text: `Θέλετε να αφαιρέσετε τη Συσκευασία "${lineObj?.containertype?.name}" ?`,
    });
    if (!answer) return false;

    try {
      await deleteContainer({ variables: { id: lineObj.id } });
      toastSuccess(t('Αφαιρέθηκε η Συσκευασία'));
    } catch (err) {
      console.error(err);
      return false;
    }

    // Remove from form
    const lines = [...values.collectionOrdersContainers];
    const idx = lines.findIndex(
      (l) => (l.id ? l.id === lineObj.id : l.tempId === lineObj.tempId), // NEW ✅
    );
    if (idx > -1) lines.splice(idx, 1);
    setFieldValue('collectionOrdersContainers', lines);

    return true;
  };

  /**
   * RENDER
   */
  return (
    <>
      <div
        className="form-group-title d-flex justify-content-between align-items-center overflow-y-hidden"
        style={{ marginBottom: '0px' }}
      >
        <h5 style={{ marginBottom: '0px' }}>{t('Πρόσθετα Στοιχεία Συσκευασιών')}</h5>
        <div className="d-flex">
          <TableEditButtons
            className="me-3"
            canEdit={canEdit}
            canInsert={canEdit}
            canDelete={canEdit}
            tableOptionsMeta={() => gridRef.current?.table.options.meta}
            twoLines={false}
          />
        </div>
      </div>

      {/* TableGrid */}
      {data ? (
        <TablePaginatedV8
          style={{ overflow: 'visible' }}
          ref={gridRef}
          loading={false}
          id="containers-grid"
          appStore={appStore}
          tableStore={tableStore}
          key="grid"
          columns={columns as any}
          onSaveRow={onSaveRow}
          data={data}
          dataCount={data.length}
          topBorderRadius={false}
          initSorting={[{ id: 'containertype.name', desc: true }]}
          onNewRow={() => {
            return {
              id: null,
              collectionOrderId: values.id,
              containertypeId: null,
              containersQty: 1,
            };
          }}
          deleteRow={handleDeleteRow}
          onRowClick={(table) => {
            const rowIdx = table.row.index;
            console.log('row click', table, rowIdx);
            setSelected({ ...table.row.original, tableRowIndex: rowIdx });
          }}
          rowProps={(row: any) => {
            const lineErr = errors.collectionOrdersContainers?.[row.index];
            let style = undefined;

            const isSelected = row.index === selected?.tableRowIndex;
            // console.log('$$$ rowProps', isSelected, row.original, selected);
            if (isSelected) style = gridRowSelectedStyle;
            if (row.original.isActive === false) style = { ...style, ...gridInactiveRowStyle };
            if (lineErr) style = { ...style, color: 'red' };

            return { style };
          }}
        />
      ) : (
        <div style={{ height: '200px' }}></div>
      )}
    </>
  );
};

